import { api } from "./api";

/**
 * Vaccination programme and dose tracking.
 *
 * The SCHEDULE is the farm's template — one entry per dose, keyed on the bird's
 * day of age, so it applies to every batch without being re-entered. A DOSE is
 * a record of something that happened to one flock. The PLAN is the two joined
 * per flock: what is overdue, what is coming up, what has been given.
 */

export type VaccinationRoute =
  | "eye_drop"
  | "drinking_water"
  | "subcutaneous"
  | "intramuscular"
  | "spray"
  | "wing_web";

/** Worst first — this is also the order a plan is most usefully read in. */
export type DoseStatus = "overdue" | "due" | "done" | "upcoming";

export interface VaccinationScheduleEntry {
  id: number;
  farm_id: number;
  day_of_age: number;
  vaccine: string;
  route: VaccinationRoute;
  notes?: string | null;
  created_at: string;
}

export interface VaccinationDose {
  id: number;
  farm_id: number;
  flock_id: number;
  schedule_id?: number | null;
  vaccine: string;
  given_on: string;
  batch?: string | null;
  notes?: string | null;
  flock_name?: string;
}

export interface VaccinationPlanRow {
  schedule_id?: number | null;
  day_of_age?: number | null;
  vaccine: string;
  route?: VaccinationRoute | null;
  notes?: string | null;
  due_date?: string | null;
  dose_id?: number | null;
  given_on?: string | null;
  batch?: string | null;
  dose_notes?: string | null;
  status: DoseStatus;
  /**
   * Days past the due date — how late a recorded dose was given, or how far
   * overdue an unrecorded one is now. Absent when there is nothing to be late
   * for (an off-programme dose, or one given on time).
   */
  days_late?: number | null;
}

export interface VaccinationPlan {
  flock_id: number;
  flock_name: string;
  age_days: number;
  overdue: number;
  due: number;
  done: number;
  upcoming: number;
  rows: VaccinationPlanRow[];
}

export interface ScheduleEntryInput {
  day_of_age: number;
  vaccine: string;
  route: VaccinationRoute;
  notes?: string | null;
}

export interface DoseInput {
  flock_id: number;
  schedule_id?: number | null;
  vaccine?: string;
  given_on: string;
  batch?: string | null;
  notes?: string | null;
}

export const VACCINATION_ROUTES: { value: VaccinationRoute; label: string }[] = [
  { value: "eye_drop", label: "Eye drop" },
  { value: "drinking_water", label: "Drinking water" },
  { value: "subcutaneous", label: "Subcutaneous" },
  { value: "intramuscular", label: "Intramuscular" },
  { value: "spray", label: "Spray" },
  { value: "wing_web", label: "Wing web" },
];

export const routeLabel = (r?: VaccinationRoute | null): string =>
  VACCINATION_ROUTES.find((x) => x.value === r)?.label ?? "—";

export const vaccinationService = {
  listSchedule: () =>
    api.get<VaccinationScheduleEntry[]>("/vaccination-schedules").then((r) => r.data),

  createScheduleEntry: (data: ScheduleEntryInput) =>
    api.post<VaccinationScheduleEntry>("/vaccination-schedules", data).then((r) => r.data),

  updateScheduleEntry: (id: number, data: ScheduleEntryInput) =>
    api.put<VaccinationScheduleEntry>(`/vaccination-schedules/${id}`, data).then((r) => r.data),

  deleteScheduleEntry: (id: number) =>
    api.delete(`/vaccination-schedules/${id}`).then((r) => r.data),

  listDoses: (flockId?: number) =>
    api
      .get<VaccinationDose[]>("/vaccination-doses", {
        params: flockId ? { flock_id: flockId } : undefined,
      })
      .then((r) => r.data),

  recordDose: (data: DoseInput) =>
    api.post<VaccinationDose>("/vaccination-doses", data).then((r) => r.data),

  deleteDose: (id: number) => api.delete(`/vaccination-doses/${id}`).then((r) => r.data),

  getFlockPlan: (flockId: number) =>
    api.get<VaccinationPlan>(`/flocks/${flockId}/vaccination-plan`).then((r) => r.data),
};

/**
 * Colour and wording for a status, in one place so the flock card and any
 * future screen cannot drift apart on what "overdue" looks like.
 *
 * THEME families only (see scripts/guards/check_theme_colors.sh): overdue is
 * `error`, due is `warning`, done is `success`, upcoming is neutral `gray`.
 */
export const STATUS_LABEL: Record<DoseStatus, string> = {
  overdue: "Overdue",
  due: "Due",
  done: "Given",
  upcoming: "Upcoming",
};

export const STATUS_TONE: Record<DoseStatus, string> = {
  overdue:
    "bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400",
  due: "bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400",
  done: "bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400",
  upcoming: "bg-gray-100 text-gray-600 dark:bg-white/[0.06] dark:text-gray-400",
};

/**
 * How a row's timing reads on screen. Kept out of the component because it is
 * the one piece of derived text on this feature with a rule behind it, and a
 * rule is worth a test.
 *
 * `days_late` on a DONE row means the dose was given late; on anything else it
 * means the due date has already passed. Same number, opposite tense.
 */
export function timingLabel(row: VaccinationPlanRow): string {
  const late = row.days_late ?? 0;
  if (row.status === "done") {
    // An off-programme dose has no due date, so "on time" would be an
    // assertion about a comparison that never happened — and it renders right
    // beside the "off programme" tag, which says as much.
    if (row.due_date == null) return "";
    return late > 0 ? `Given ${late} ${dayWord(late)} late` : "Given on time";
  }
  if (row.status === "overdue") {
    return `${late} ${dayWord(late)} overdue`;
  }
  if (row.status === "due") return "Due now";
  return "Not yet due";
}

const dayWord = (n: number) => (n === 1 ? "day" : "days");

/**
 * Client-side mirror of the server's schedule validation, so a mistake shows
 * while typing rather than after a failed save. The server revalidates — this
 * is a convenience, not the guarantee.
 */
export function validateScheduleEntry(entry: {
  day_of_age: number;
  vaccine: string;
  route: string;
}): string | null {
  if (!entry.vaccine.trim()) return "Enter the vaccine name.";
  if (entry.vaccine.trim().length > 120) return "That vaccine name is too long.";
  if (!Number.isInteger(entry.day_of_age) || entry.day_of_age < 0 || entry.day_of_age > 1000) {
    return "Day of age must be a whole number between 0 and 1000.";
  }
  if (!VACCINATION_ROUTES.some((r) => r.value === entry.route)) {
    return "Choose how the vaccine is given.";
  }
  return null;
}
